import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { cn } from '@/lib/utils';

export interface CopyButtonProps {
  value: string;
  /** What is being copied, for the tooltip and the screen reader: "Copiar RUT". */
  what?: string;
  /** Renders the label next to the icon instead of an icon-only button. */
  withLabel?: boolean;
  size?: 'sm' | 'default' | 'icon' | 'icon-sm';
  variant?: 'ghost' | 'outline' | 'secondary';
  className?: string;
}

export function CopyButton({
  value,
  what,
  withLabel = false,
  size = 'icon-sm',
  variant = 'ghost',
  className,
}: CopyButtonProps) {
  const { copy, copied } = useCopyToClipboard();
  const label = what ? `Copiar ${what}` : 'Copiar';

  const icon = copied ? (
    <Check className="size-3.5 text-success" />
  ) : (
    <Copy className="size-3.5" />
  );
  const shared = {
    type: 'button',
    variant,
    className: cn('text-muted-foreground hover:text-foreground', className),
    onClick: (event: React.MouseEvent) => {
      event.stopPropagation();
      void copy(value);
    },
  } as const;

  // Split the two shapes rather than computing `size`: Button's props are a
  // discriminated union (icon sizes demand an aria-label) and a ternary
  // collapses that discrimination.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {withLabel ? (
            <Button {...shared} size={size === 'icon' || size === 'icon-sm' ? 'sm' : size}>
              {icon}
              <span>{copied ? 'Copiado' : label}</span>
            </Button>
          ) : (
            <Button {...shared} size={size === 'sm' || size === 'default' ? 'icon-sm' : size} aria-label={label}>
              {icon}
            </Button>
          )}
        </TooltipTrigger>
        <TooltipContent>{copied ? 'Copiado' : label}</TooltipContent>
      </Tooltip>
      <span aria-live="polite" className="sr-only">
        {copied ? `${what ?? 'Texto'} copiado al portapapeles` : ''}
      </span>
    </TooltipProvider>
  );
}
